import type { Participant } from '@seen/core/models/patients/participant'
import { ExtendedQuestionnaireResponse } from '@seen/core/models/questionnaireResponse'
import * as questionnaires from '@seen/core/models/questionnaires/index'
import { IconBowlChopsticks, IconSoup } from '@tabler/icons-react'
import type { JSX } from 'react'
import {
  FacesheetCard,
  FacesheetCardActions,
  FacesheetCardCollapseTrigger,
  FacesheetCardContent,
  FacesheetCardCount,
  FacesheetCardHeader,
  FacesheetCardIcon,
  FacesheetCardLabel,
  FacesheetCardTitle,
} from '#src/components/patient/detail-content/facesheet/index.ts'
import { PrintMealTicket } from '#src/components/patient/ops/PrintMealTicket.tsx'
import { QuestionnaireForm } from '#src/components/questionnaire-form/QuestionnaireForm.tsx'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'
import { extractBlockLinesFromHTML } from '#src/utils/html.ts'

// The PCP view of the card shows only fluid and supplement data, read-only
// (Figma 814-9952, "Simplified card view for PCP").
const PCP_DIET_ROWS = [
  { linkId: 'fluid-consistency', label: 'Fluid Consistency' },
  { linkId: 'fluid-restrictions', label: 'Fluid Restrictions', unit: 'mL/d' },
  { linkId: 'supplements', label: 'Supplements' },
]

export interface DietCardProps {
  participant: Participant
  variant?: 'full' | 'pcp'
}

export function DietCard({
  participant,
  variant = 'full',
}: DietCardProps): JSX.Element {
  if (variant === 'pcp') {
    return <PcpDietCard participant={participant} />
  }
  return (
    <FacesheetCard rumName="diet">
      <FacesheetCardHeader>
        <FacesheetCardTitle>
          <FacesheetCardIcon>
            <IconBowlChopsticks />
          </FacesheetCardIcon>
          <FacesheetCardLabel>Diet Needs and Support</FacesheetCardLabel>
          <FacesheetCardCollapseTrigger />
        </FacesheetCardTitle>
        <FacesheetCardActions>
          <PrintMealTicket participant={participant} />
        </FacesheetCardActions>
      </FacesheetCardHeader>
      <FacesheetCardContent>
        <QuestionnaireForm
          questionnaire={questionnaires.DietQuestionnaire}
          participant={participant}
        />
      </FacesheetCardContent>
    </FacesheetCard>
  )
}

function PcpDietCard({
  participant,
}: {
  participant: Participant
}): JSX.Element {
  // A plain search, not QuestionnaireForm's search-or-create: this card only
  // reads, so opening it must not create a response.
  const { data: qr } = useSearchOne(
    ExtendedQuestionnaireResponse,
    ExtendedQuestionnaireResponse.searchQuery({
      questionnaire: questionnaires.DietQuestionnaire,
      subject: participant.reference,
    })
  )
  const rows = PCP_DIET_ROWS.flatMap((row) => {
    const value = answerText(qr ?? null, row.linkId)
    return value ? [{ ...row, value }] : []
  })

  return (
    <FacesheetCard rumName="diet">
      <FacesheetCardHeader>
        <FacesheetCardTitle>
          <FacesheetCardIcon>
            <IconSoup />
          </FacesheetCardIcon>
          <FacesheetCardLabel>Diet Needs &amp; Support</FacesheetCardLabel>
          <FacesheetCardCollapseTrigger />
        </FacesheetCardTitle>
        <FacesheetCardActions>
          <FacesheetCardCount>{rows.length}</FacesheetCardCount>
          <PrintMealTicket participant={participant} />
        </FacesheetCardActions>
      </FacesheetCardHeader>
      <FacesheetCardContent>
        {/* mt-1 on top of the card's 8px header gap makes Figma's 12px. */}
        {rows.length > 0 ? (
          <ul
            aria-label="Fluids and supplements"
            className="mt-1 flex flex-col gap-1"
          >
            {rows.map((row) => (
              <li
                key={row.linkId}
                className="text-xs leading-4 tracking-[-0.01em] text-stone-950 underline dark:text-foreground"
              >
                {row.label}:{' '}
                <strong className="font-semibold">
                  {row.unit && /^\d+(\.\d+)?$/.test(row.value)
                    ? `${row.value} ${row.unit}`
                    : row.value}
                </strong>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1 text-center text-xs leading-4 text-stone-500">
            No data
          </p>
        )}
      </FacesheetCardContent>
    </FacesheetCard>
  )
}

// Text answers are rich-text HTML (older ones are plain text); an editor
// cleared back to an empty paragraph counts as no answer.
function answerText(
  qr: ExtendedQuestionnaireResponse | null,
  linkId: string
): string {
  const value = qr?.getAnswerValue(linkId)
  return typeof value === 'string'
    ? extractBlockLinesFromHTML(value).join(' ')
    : ''
}
